// Platform overview — Web Portal v2.
//
// Every figure here is a query against /admin/stats/overview. The previous
// version hardcoded them — "287 users", "42 open requests", "HTG 184k pending
// payouts", "+12 in last 7 days" — under a note saying real numbers would
// arrive later. Operational numbers nobody can act on are worse than none:
// they look authoritative, and whoever runs the platform would decide on
// them.
//
// Where a number cannot be derived the UI shows a dash, not a guess.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Loader2, AlertTriangle } from 'lucide-react';
import { PortalShell } from '../components/shared/PortalShell';
import { ADMIN_NAV } from './_navs';
import { Card, EmptyState, StatusPill } from '../components/shared/primitives';
import { adminStats, type AdminOverview, type AdminOverviewKpi } from '../lib/api';
import { formatHtgMinor } from '../lib/money';

const RANGES = [
  { key: '14d', label: '14 days' },
  { key: '30d', label: '30 days' },
  { key: 'quarter', label: 'Quarter' },
] as const;

const usd = (minor: number) => `$${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function AdminDashboard() {
  const [range, setRange] = useState<string>('14d');

  const q = useQuery({
    queryKey: ['admin', 'overview', range],
    queryFn: () => adminStats.overview(range),
    // Operational figures should not sit stale on a wall display.
    refetchInterval: 60_000,
  });

  return (
    <PortalShell
      nav={ADMIN_NAV}
      activeId="overview"
      title="Platform overview"
      subtitle="All markets · all merchants · times in UTC"
      actions={
        <div className="flex gap-1 rounded-xl border border-[var(--color-border)] bg-white p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              aria-pressed={range === r.key}
              className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-[12.5px] transition-colors ${
                range === r.key
                  ? 'bg-[var(--color-ink)] font-extrabold text-white'
                  : 'font-bold text-[var(--color-secondary-2)] hover:text-[var(--color-ink)]'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      }
    >
      {q.isLoading ? (
        <Card><div className="py-16 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-[var(--color-muted)]" aria-hidden="true" /></div></Card>
      ) : q.error ? (
        <Card>
          <p className="inline-flex items-center gap-2 text-[13px] text-[var(--color-danger-fg)]">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            {(q.error as Error).message || 'Could not load platform statistics.'}
          </p>
        </Card>
      ) : q.data ? (
        <Overview data={q.data} />
      ) : null}
    </PortalShell>
  );
}

function Overview({ data }: { data: AdminOverview }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3.5 grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))]">
        {data.kpis.map((k) => <KpiCard key={k.key} kpi={k} series={data.gmv_series} />)}
      </div>

      <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))]">
        <GmvChart series={data.gmv_series} days={data.range.days} />
        <Attention rows={data.attention} />
      </div>

      <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))]">
        <LatestRequests rows={data.latest_requests} />
        <Providers rows={data.providers} />
      </div>
    </div>
  );
}

// ─── KPI ─────────────────────────────────────────────────────

function KpiCard({ kpi, series }: { kpi: AdminOverviewKpi; series: AdminOverview['gmv_series'] }) {
  const value =
    kpi.value_usd_minor !== undefined ? usd(kpi.value_usd_minor)
    : kpi.value_pct !== undefined ? (kpi.value_pct === null ? '—' : `${kpi.value_pct}%`)
    : String(kpi.value ?? 0);

  // The sparkline only means anything for the volume KPI; drawing the same
  // shape under an unrelated number would imply a trend that is not there.
  const spark = kpi.key === 'gmv'
    ? series.map((d) => d.groceries_usd_minor + d.airtime_usd_minor)
    : null;

  return (
    <div className="rounded-[20px] border border-[var(--color-border)] bg-white px-5 py-[18px]">
      <p className="text-[12.5px] font-semibold text-[var(--color-secondary-2)]">{kpi.label}</p>
      <div className="mt-1.5 flex flex-wrap items-baseline gap-2">
        <span className="text-[28px] font-extrabold tracking-[-0.9px] tabular-nums text-[var(--color-ink)]">{value}</span>
        {kpi.delta_pct !== null && (
          <span className={`whitespace-nowrap text-[12px] font-extrabold ${
            kpi.delta_pct >= 0 ? 'text-[var(--color-success-fg)]' : 'text-[var(--color-danger-fg)]'
          }`}>
            {kpi.delta_pct >= 0 ? '+' : ''}{kpi.delta_pct}%
          </span>
        )}
      </div>
      {spark && <Sparkline values={spark} />}
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return <div className="mt-2.5 h-9" />;

  const max = Math.max(...values, 1);
  const pts = values
    .map((v, i) => `${(i / (values.length - 1)) * 120},${32 - (v / max) * 30}`)
    .join(' ');

  return (
    <svg viewBox="0 0 120 32" preserveAspectRatio="none" className="mt-2.5 block h-9 w-full" aria-hidden="true">
      <polyline points={pts} fill="none" stroke="var(--color-interactive)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ─── Volume chart ────────────────────────────────────────────

function GmvChart({ series, days }: { series: AdminOverview['gmv_series']; days: number }) {
  const max = Math.max(...series.map((d) => d.groceries_usd_minor + d.airtime_usd_minor), 1);
  const empty = series.every((d) => d.groceries_usd_minor + d.airtime_usd_minor === 0);

  return (
    <section className="rounded-[22px] border border-[var(--color-border)] bg-white p-[22px]">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-[17px] font-extrabold text-[var(--color-ink)]">Gross volume by product</h2>
          <p className="mt-0.5 text-[12.5px] text-[var(--color-secondary-2)]">Last {days} days · USD</p>
        </div>
        <div className="flex gap-3.5 text-[12px] text-[var(--color-secondary)]">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px] bg-brand-600" />Groceries</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px] bg-brand-300" />Airtime</span>
        </div>
      </div>

      {empty ? (
        <p className="py-14 text-center text-[13px] text-[var(--color-muted)]">
          No paid orders in this window.
        </p>
      ) : (
        <>
          <div className="mt-5 flex h-[200px] items-end gap-2.5 border-b border-[var(--color-hairline)]">
            {series.map((d) => {
              const total = d.groceries_usd_minor + d.airtime_usd_minor;
              return (
                <div
                  key={d.date}
                  className="flex h-full flex-1 flex-col justify-end"
                  title={`${d.date} · ${usd(total)}`}
                >
                  {d.airtime_usd_minor > 0 && (
                    <div
                      className="flex-none rounded-t-[5px] border-b-2 border-white bg-brand-300"
                      style={{ height: `${(d.airtime_usd_minor / max) * 100}%` }}
                    />
                  )}
                  {d.groceries_usd_minor > 0 && (
                    <div className="flex-none bg-brand-600" style={{ height: `${(d.groceries_usd_minor / max) * 100}%` }} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between font-mono text-[11.5px] text-[var(--color-muted)]">
            <span>{series[0]?.date}</span>
            <span>{series[series.length - 1]?.date}</span>
          </div>
        </>
      )}
    </section>
  );
}

// ─── Needs attention ─────────────────────────────────────────

const TONE: Record<string, string> = {
  warning: 'border-[var(--color-warning-border)] bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]',
  danger: 'border-[#F3C7C7] bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)]',
  brand: 'border-[var(--color-tint-2)] bg-[var(--color-tint)] text-brand-600',
};

function Attention({ rows }: { rows: AdminOverview['attention'] }) {
  return (
    <section className="rounded-[22px] border border-[var(--color-border)] bg-white p-[22px]">
      <h2 className="text-[17px] font-extrabold text-[var(--color-ink)]">Needs attention</h2>

      {rows.length === 0 ? (
        // An empty queue is the good outcome and should read that way.
        <p className="py-10 text-center text-[13px] text-[var(--color-muted)]">
          Nothing waiting. Every queue is clear.
        </p>
      ) : (
        <div className="mt-3.5 flex flex-col gap-2.5">
          {rows.map((x) => (
            <Link
              key={x.key}
              to={x.href}
              className={`flex items-center gap-3 rounded-[14px] border px-3.5 py-3 text-left transition-opacity hover:opacity-90 ${TONE[x.tone] ?? TONE.brand}`}
            >
              <span className="w-8 text-[22px] font-extrabold tabular-nums">{x.count}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-extrabold text-[var(--color-ink)]">{x.title}</span>
                <span className="mt-px block text-[12px] text-[var(--color-secondary-2)]">{x.sub}</span>
              </span>
              <ChevronRight className="h-3.5 w-3.5 flex-none text-[var(--color-muted)]" aria-hidden="true" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Latest requests ─────────────────────────────────────────

function LatestRequests({ rows }: { rows: AdminOverview['latest_requests'] }) {
  return (
    <section className="overflow-hidden rounded-[22px] border border-[var(--color-border)] bg-white">
      <div className="flex items-baseline justify-between border-b border-[var(--color-hairline)] px-[22px] py-[18px]">
        <h2 className="text-[17px] font-extrabold text-[var(--color-ink)]">Latest requests</h2>
        <Link to="/admin/requests" className="text-[13px] font-extrabold text-[var(--color-interactive)] hover:underline">
          Open queue
        </Link>
      </div>

      {rows.length === 0 ? (
        <div className="p-6">
          <EmptyState icon={AlertTriangle} title="No requests yet" body="Orders will appear here as customers submit them." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-left">
            <thead className="bg-[#FAFBFE] text-[11px] font-extrabold uppercase tracking-[0.7px] text-[var(--color-muted)]">
              <tr>
                <th className="px-[22px] py-2.5">Reference</th>
                <th className="px-3 py-2.5">Recipient</th>
                <th className="px-3 py-2.5 text-right">Cart</th>
                <th className="px-[22px] py-2.5">State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#F2F4F9]">
                  <td className="px-[22px] py-3 font-mono text-[12.5px] text-[var(--color-body)]">{r.reference}</td>
                  <td className="px-3 py-3 text-[13px] font-semibold text-[var(--color-ink)]">{r.recipient ?? '—'}</td>
                  <td className="px-3 py-3 text-right text-[13px] font-extrabold tabular-nums text-[var(--color-ink)]">
                    {r.subtotal_htg_minor > 0 ? formatHtgMinor(r.subtotal_htg_minor) : '—'}
                  </td>
                  <td className="px-[22px] py-3"><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─── Provider health ─────────────────────────────────────────

/**
 * Configuration state, not live latency.
 *
 * A dot that goes green because we pinged a provider once would be exactly
 * the kind of reassuring-but-meaningless signal this screen was rebuilt to
 * remove. "test" and "sandbox" are called out because shipping while a
 * payment provider is in test mode is a real and easy mistake.
 */
const PROVIDER_STATE: Record<string, { label: string; dot: string }> = {
  live: { label: 'Live', dot: 'bg-[var(--color-success-fg)]' },
  test: { label: 'Test mode', dot: 'bg-[var(--color-warning-dot)]' },
  sandbox: { label: 'Sandbox', dot: 'bg-[var(--color-warning-dot)]' },
  not_configured: { label: 'Not connected', dot: 'bg-[var(--color-muted)]' },
};

function Providers({ rows }: { rows: AdminOverview['providers'] }) {
  return (
    <section className="rounded-[22px] border border-[var(--color-border)] bg-white p-[22px]">
      <h2 className="text-[17px] font-extrabold text-[var(--color-ink)]">Provider health</h2>
      <div className="mt-3.5 flex flex-col">
        {rows.map((p, i) => {
          const s = PROVIDER_STATE[p.state] ?? PROVIDER_STATE.not_configured;
          return (
            <div key={p.name} className={`flex items-center gap-3 py-3 ${i ? 'border-t border-[var(--color-hairline)]' : ''}`}>
              <span className={`h-2.5 w-2.5 flex-none rounded-full ${s.dot}`} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-bold text-[var(--color-ink)]">{p.name}</p>
                <p className="truncate text-[12px] text-[var(--color-muted)]">{p.role}</p>
              </div>
              <span className="flex-none font-mono text-[12px] text-[var(--color-secondary)]">{s.label}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
